import dynamic from 'next/dynamic'
import {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import { toFeatureCollection } from './MapContainer.helpers'
import {
  MIN_ZOOM,
  DEFAULT_ZOOM
} from '@/modules/accredited-establishments/constants'
import {
  BRAZIL_BOUNDS,
  DEFAULT_CENTER
} from '@/modules/accredited-establishments/components/Maps/constants'
import { useMarkers } from '@/modules/accredited-establishments/hooks'
import { MapEvent, useMap } from '@vis.gl/react-google-maps'
import { useDispatchPageEvent, useDrawer } from '@/global/hooks'
import {
  ClusteredMarkers,
  EstablishmentGeoJson,
  Leaves
} from '@/modules/accredited-establishments/components/Maps/Supercluster'

import { Position } from 'geojson'
import { areAllInSamePosition } from '@/modules/accredited-establishments/functions'
import { debounce } from 'lodash'
import { Skeleton } from '@/global/components/Skeleton'
import { EventName } from '@/global/utils/analytics-events/types/common'
import { EstablishmentCategory } from '@/modules/accredited-establishments/api/types'
import {
  MapContainerActions,
  MapContainerLoader,
  ApiErrorMessageModal,
  CLUSTER_ITEMS_LIST_PANEL_ID
} from '@/modules/accredited-establishments/components/Maps'

const GoogleMap = dynamic(
  () => import('@vis.gl/react-google-maps').then(module => module.Map),
  {
    ssr: false
  }
)

const ClusterItemsListPanel = dynamic(
  () =>
    import('../ClusterItemsListPanel').then(
      module => module.ClusterItemsListPanel
    ),
  {
    ssr: false
  }
)

const NotFoundModal = dynamic(
  () =>
    import(
      '@/modules/accredited-establishments/components/Maps/NotFoundModal'
    ).then(module => module.NotFoundModal),
  {
    ssr: false
  }
)
interface MapContainerProps {
  onDetails: ({
    referencedId,
    category
  }: {
    referencedId: string
    category: EstablishmentCategory
  }) => void
}

const MapContainer: FunctionComponent<MapContainerProps> = ({ onDetails }) => {
  /*
    We should avoid searching after the first zoom changed event 
    when the map has been repositioned due to a change in location input.
  */
  const [
    firstZoomChangeAfterLoadWithAddress,
    setFirstZoomChangeAfterLoadWithAddress
  ] = useState(true)
  const [mapLoaded, setMapLoaded] = useState(false)

  const { open: openClusterItemsListModal } = useDrawer(
    CLUSTER_ITEMS_LIST_PANEL_ID
  )

  const map = useMap()

  useDispatchPageEvent({
    eventName: EventName.PAGE_VIEWED,
    pageTitle: 'Mapa da Rede Credenciada'
  })

  const {
    view,
    markerLocation,
    markers,
    searchByLatLon,
    searchClusterItems,
    isFetching
  } = useMarkers()

  /*
    Whenever we change the marker location to address or the view we must reset the indicator
  */
  useEffect(() => {
    if (markerLocation.type === 'coordinates') {
      setFirstZoomChangeAfterLoadWithAddress(false)
    } else if (view === 'list' || markerLocation.type === 'address') {
      setFirstZoomChangeAfterLoadWithAddress(true)
    }
  }, [view, markerLocation])

  const onDragEnd = useCallback(
    (event: MapEvent) => {
      const center = event.map.getCenter()?.toJSON()

      if (!center) return

      searchByLatLon({
        lat: center.lat.toString(),
        lon: center.lng.toString(),
        bounds: event.map.getBounds()?.toJSON()
      })
    },
    [map]
  )

  const onZoomChanged = useCallback(
    (event: MapEvent) => {
      if (view === 'list') return

      if (firstZoomChangeAfterLoadWithAddress) {
        return setFirstZoomChangeAfterLoadWithAddress(false)
      }

      const zoom = event.map.getZoom()

      if (!zoom || !map) return

      const center = event.map.getCenter()?.toJSON()

      if (!center) return

      searchByLatLon({
        lat: center.lat.toString(),
        lon: center.lng.toString(),
        bounds: event.map.getBounds()?.toJSON()
      })
    },
    [map, view, firstZoomChangeAfterLoadWithAddress]
  )

  const debouncedOnZoomChanged = useMemo(
    () => debounce(onZoomChanged, 1000),
    [onZoomChanged]
  )

  const debouncedOnDragEnd = useMemo(
    () => debounce(onDragEnd, 1000),
    [onDragEnd]
  )

  const onClusterClick = (position: Position, leaves: Leaves) => {
    const zoom = map?.getZoom()

    if (!zoom || !map) return

    const [lon, lat] = position

    const leavesPositions = leaves.map(leaf => ({
      lat: leaf.geometry.coordinates[1],
      lng: leaf.geometry.coordinates[0]
    }))

    if (areAllInSamePosition(leavesPositions)) {
      searchClusterItems({ lat: lat.toString(), lon: lon.toString() })
      openClusterItemsListModal()
    } else {
      map.setCenter({ lat, lng: lon })
      map.setZoom(zoom + 1)
    }
  }

  const geojson: EstablishmentGeoJson = useMemo(() => {
    return toFeatureCollection(markers)
  }, [markers])

  return (
    <div className='w-full h-full relative lg:rounded-t-gl-border-radius-lg overflow-hidden'>
      {!mapLoaded ? (
        <Skeleton
          width='w-full'
          height='h-full'
          variant='rectangular'
        />
      ) : null}

      <GoogleMap
        disableDefaultUI
        mapId={process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        gestureHandling='greedy'
        minZoom={MIN_ZOOM}
        noClear
        restriction={{
          strictBounds: true,
          latLngBounds: BRAZIL_BOUNDS
        }}
        onTilesLoaded={() => setMapLoaded(true)}
        renderingType='VECTOR'
        clickableIcons={false}
        onDragend={debouncedOnDragEnd}
        onZoomChanged={debouncedOnZoomChanged}
      >
        {geojson ? (
          <ClusteredMarkers
            geojson={geojson}
            onClusterClick={(cluster, _, leaves) =>
              onClusterClick(cluster.geometry.coordinates, leaves)
            }
            onMarkerClick={marker =>
              onDetails({
                referencedId: String(marker.id),
                category: marker.properties.category
              })
            }
          />
        ) : null}
      </GoogleMap>

      {mapLoaded ? <MapContainerActions /> : null}

      <ClusterItemsListPanel onEstablishmentClick={onDetails} />

      <NotFoundModal />

      <ApiErrorMessageModal />

      <MapContainerLoader isLoading={isFetching} />
    </div>
  )
}

const MemoizedMapContainer = memo(MapContainer)

export { MemoizedMapContainer as MapContainer }
